import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EditNoteIcon from '@mui/icons-material/EditNote';
import MicIcon from '@mui/icons-material/Mic';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LockIcon from '@mui/icons-material/Lock';
import goldColors from '../themes/goldColors';
import { useLanguage } from '../contexts/LanguageContext';

// 12 ago 2026 (pedido de Augusto): esta página era literalmente
// "En construcción" (UnderConstruction) desde el principio -- nunca tuvo
// contenido real. Augusto pidió una guía completa "para tontos": qué es
// PCM, para qué sirve, y todos los procesos, explicados paso a paso sin
// dar por sentado ningún conocimiento previo.
//
// Se escribió solo en español a propósito (no se tradujo cada párrafo al
// inglés/portugués como el resto del sitio) -- es contenido largo y el
// público real de PCM hoy es de habla hispana (ver
// PCM_estado_del_proyecto.md, Darwin Optima opera en Lima, Perú). Si en
// algún momento hay clientes que necesiten esto en otro idioma, conviene
// traducirlo entero en un solo paso en vez de mantener dos versiones
// parciales desde ahora.
//
// 3 sep 2026 (pedido de Augusto: "actualiza la parte de Guías"): esta
// guía se escribió el 12 ago 2026 y desde entonces se agregaron/
// cambiaron varias cosas que no estaban reflejadas acá -- se agrega la
// sección de Categorías (página propia desde el 25 ago) y de Presupuesto
// (no existía ninguna sección para esto, pese a ser una función central
// de la app), se actualiza Calendario (toggle de moneda + edición/
// borrado de movimientos), Recurrentes (columnas separadas Soles/
// Dólares), Informes (qué es gratis vs. qué es exclusivo de un plan), y
// Suscripción y planes (antes solo mencionaba el trial, sin explicar qué
// cambia entre planes). También se quita la mención al Blog en la
// sección 3 (ver NotionSidebar.js/routes.js -- Augusto pidió quitar esa
// sección porque nunca tuvo contenido real).
//
// 4 sep 2026 (pedido de Augusto: "no la noto tan completa, puede incluir
// gráficos que permitan aprender mejor?"): dos cambios en esta pasada --
// (a) contenido: se agrega la sección nueva "6. Transferencias entre
// cuentas y tarjetas" (con el botón Transferencia agregado a Nueva
// Transacción el 3 sep), se documenta el switch "Cuenta de reserva /
// ahorro intocable" en Cuentas y su efecto en Calendario/Transacciones
// (agregado el 4 sep), y se agregan a Informes los tres reportes nuevos
// (Gasto por categoría, Presupuesto vs. gastado, Deuda) -- todo esto
// existía en la app pero no estaba en la guía. Al agregar la sección de
// Transferencias, TODAS las secciones de acá para abajo corren un
// número (Deudas pasa de 7 a 8, Suscripción de 13 a 14, etc.) -- ojo con
// esto si se vuelve a reordenar en el futuro, hay referencias cruzadas
// ("ver sección N") que hay que actualizar a mano.
// (b) gráficos: esta página era solo texto y listas -- se agregan varios
// diagramas simples (con SVG/MUI, sin imágenes externas ni capturas de
// pantalla que se desactualizarían solas) para que se entienda de un
// vistazo: las 3 preguntas que responde PCM, los pasos iniciales como
// línea de tiempo (Stepper), las 3 formas de registrar un movimiento
// como flujo, cómo se mueve el dinero en una Transferencia, cómo se ve
// el "piso fijo" dentro de un Presupuesto, un maquetado del detalle de
// un día en Calendario, y una vista previa de cómo lucen los gráficos de
// Informes (barras/circular) antes de entrar ahí.
const Section = ({ icon: Icon, title, defaultExpanded, children }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 1, '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {Icon && <Icon sx={{ color: goldColors.main }} />}
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
  </Accordion>
);

const Paso = ({ n, children }) => (
  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
    <ListItemIcon sx={{ minWidth: 32, mt: 0.3 }}>
      <Chip label={n} size="small" sx={{ bgcolor: goldColors.main, color: '#fff', fontWeight: 600, height: 22, minWidth: 22 }} />
    </ListItemIcon>
    <ListItemText primary={children} />
  </ListItem>
);

const Punto = ({ children }) => (
  <ListItem sx={{ py: 0.3 }}>
    <ListItemIcon sx={{ minWidth: 28 }}>
      <FiberManualRecordIcon sx={{ fontSize: 8, color: goldColors.main }} />
    </ListItemIcon>
    <ListItemText primary={children} />
  </ListItem>
);

// 4 sep 2026: piezas visuales reutilizables para los diagramas de esta
// página -- todas construidas con MUI/SVG plano (sin capturas de
// pantalla reales, que se desactualizarían cada vez que cambie el
// diseño de la app).
const FlowCard = ({ icon: Icon, title, subtitle, color }) => (
  <Paper
    variant="outlined"
    sx={{ p: 1.5, textAlign: 'center', flex: '1 1 130px', minWidth: 130, borderColor: color || goldColors.light }}
  >
    <Icon sx={{ fontSize: 28, color: color || goldColors.main, mb: 0.5 }} />
    <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{subtitle}</Typography>
    )}
  </Paper>
);

const FlowArrow = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5, py: { xs: 0.5, sm: 0 } }}>
    <ArrowForwardIcon sx={{ color: goldColors.main, transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
  </Box>
);

// Gráfico de barras genérico (números inventados, solo para mostrar CÓMO
// se ve un gráfico de Informes antes de entrar a esa sección).
const MiniBarChart = () => (
  <svg width="110" height="70" viewBox="0 0 110 70" role="img" aria-label="Ejemplo de gráfico de barras">
    <line x1="0" y1="65" x2="110" y2="65" stroke="#ccc" strokeWidth="1" />
    <rect x="5" y="30" width="18" height="35" rx="2" fill={goldColors.light} />
    <rect x="32" y="15" width="18" height="50" rx="2" fill={goldColors.main} />
    <rect x="59" y="40" width="18" height="25" rx="2" fill="#4caf50" />
    <rect x="86" y="8" width="18" height="57" rx="2" fill="#f44336" />
  </svg>
);

// Gráfico circular genérico (técnica de "donut" con stroke-dasharray
// sobre un círculo de radio 16 -- su circunferencia, ~100, se trata como
// una escala de 0 a 100, así los valores de dasharray se leen casi como
// porcentajes).
const MiniPieChart = () => (
  <svg width="70" height="70" viewBox="0 0 36 36" role="img" aria-label="Ejemplo de gráfico circular">
    <circle r="16" cx="18" cy="18" fill="transparent" stroke={goldColors.pale} strokeWidth="4" />
    <circle r="16" cx="18" cy="18" fill="transparent" stroke={goldColors.main} strokeWidth="4" strokeDasharray="40 100" strokeDashoffset="0" />
    <circle r="16" cx="18" cy="18" fill="transparent" stroke="#4caf50" strokeWidth="4" strokeDasharray="25 100" strokeDashoffset="-40" />
    <circle r="16" cx="18" cy="18" fill="transparent" stroke="#f44336" strokeWidth="4" strokeDasharray="20 100" strokeDashoffset="-65" />
  </svg>
);

// Barra de ejemplo para explicar visualmente el "piso fijo" dentro de un
// Presupuesto (ver sección 9) -- pisoPct es el % de ejemplo, no un dato
// real de ninguna cuenta.
const BudgetBar = ({ pisoPct = 45 }) => (
  <Box sx={{ maxWidth: 320, my: 1.5 }}>
    <Box sx={{ display: 'flex', height: 22, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ width: `${pisoPct}%`, bgcolor: goldColors.main }} />
      <Box sx={{ width: `${100 - pisoPct}%`, bgcolor: goldColors.pale }} />
    </Box>
    <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: goldColors.main }} />
        <Typography variant="caption" color="text.secondary">Piso fijo (gastos recurrentes ya comprometidos)</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: goldColors.pale, border: '1px solid', borderColor: 'divider' }} />
        <Typography variant="caption" color="text.secondary">Margen que todavía puedes gastar</Typography>
      </Box>
    </Box>
  </Box>
);

// Maquetado (números inventados) de cómo se ve el detalle de un día en
// el Calendario, con el desglose por cuenta dentro del mismo día -- ver
// sección 11.
const DiaMockup = () => (
  <Paper variant="outlined" sx={{ p: 1.5, maxWidth: 340, my: 1.5 }}>
    <Typography variant="caption" color="text.secondary">31 de mayo (ejemplo)</Typography>
    <Typography variant="body2" sx={{ mb: 1 }}>
      Balance de inicio: <strong>S/ 4,850.00</strong> · Balance de fin: <strong>S/ 5,120.00</strong>
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      <Chip size="small" variant="outlined" label="BCP Ahorros: S/ 3,400.00" />
      <Chip size="small" variant="outlined" label="Efectivo: S/ 1,720.00" />
    </Box>
  </Paper>
);

const pasosIniciales = [
  {
    title: 'Crea tu cuenta',
    body: 'Entra a la página de PCM y haz clic en "Crear cuenta". Completa tu nombre, correo, contraseña, país (para el código telefónico) y número de teléfono. También puedes registrarte directo con tu cuenta de Google, si está disponible.',
  },
  {
    title: 'Sigue la bienvenida',
    body: 'Verás una pantalla de bienvenida que te explica 3 pasos cortos: Perfil, Cuentas y Deudas. Tómalos con calma -- toma 3 a 5 minutos en total, y puedes completar lo que falte más adelante.',
  },
  {
    title: 'Completa tu Perfil',
    body: 'Un par de datos rápidos (ocupación, estado civil, si tienes dependientes) para que PCM adapte los cálculos a tu situación.',
  },
  {
    title: 'Registra tus Cuentas bancarias',
    body: 'Registra tus cuentas (ahorros, corriente, efectivo) para que PCM sepa cuánto dinero real tienes disponible -- ver sección 4 más abajo.',
  },
  {
    title: 'Registra tus Deudas (si tienes)',
    body: 'Si tienes tarjetas de crédito o préstamos, regístralos. Si no tienes ninguno, simplemente avanza -- ver sección 8.',
  },
  {
    title: '¡Listo!',
    body: 'Ya puedes empezar a registrar tus movimientos del día a día.',
  },
];

const GuidesPage = () => {
  const { language } = useLanguage();

  if (language !== 'es') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          This guide is currently only available in Spanish. Switch the language selector to
          "Español" to read it, or write to support and we'll help you directly.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3, pb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <MenuBookIcon sx={{ fontSize: 34, color: goldColors.main }} />
        <Typography variant="h4" fontWeight="bold">Guía completa de PCM</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Todo lo que necesitas saber para usar PCM, explicado paso a paso. No necesitas saber nada
        de finanzas ni de tecnología para seguir esta guía.
      </Typography>

      <Section icon={CheckCircleIcon} title="1. ¿Qué es PCM y para qué sirve?" defaultExpanded>
        <Typography paragraph>
          <strong>PCM</strong> significa <strong>Planificación, Control y Monitoreo</strong>. Es una
          herramienta para que veas, en un solo lugar, cuánto dinero tienes, cuánto gastas, cuánto
          debes y hacia dónde va tu negocio o tus finanzas personales -- sin depender de hojas de
          cálculo sueltas ni de la memoria.
        </Typography>
        <Typography paragraph sx={{ mb: 1 }}>El objetivo de PCM es responder, en cualquier momento, tres preguntas:</Typography>
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <FlowCard icon={AccountBalanceWalletIcon} title="¿Cuánto tengo?" subtitle="Saldo real de tus cuentas y efectivo" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FlowCard icon={CreditCardIcon} title="¿Cuánto debo?" subtitle="Tarjetas y préstamos, con sus vencimientos" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FlowCard icon={TrendingUpIcon} title="¿Hacia dónde va mi dinero?" subtitle="En qué categorías gastas más" />
          </Grid>
        </Grid>
        <Typography paragraph sx={{ mt: 1 }}>
          Para lograrlo, tú registras tus movimientos (a mano, por voz, o por WhatsApp) y PCM hace
          las cuentas, los gráficos y los resúmenes por ti.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="2. Primeros pasos: registro y configuración inicial">
        <Stepper orientation="vertical" nonLinear sx={{ mt: 1 }}>
          {pasosIniciales.map((p, idx) => (
            <Step key={idx} active expanded>
              <StepLabel StepIconProps={{ sx: { color: `${goldColors.main} !important` } }}>
                <Typography fontWeight={600}>{p.title}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary">{p.body}</Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Section>

      <Section icon={CheckCircleIcon} title="3. El menú principal: qué encuentras en cada sección">
        <Typography paragraph>
          El menú de la izquierda (sidebar) es tu punto de partida para todo. Está organizado en
          bloques:
        </Typography>
        <List dense>
          <Punto><strong>Inicio</strong> -- un resumen rápido de tu situación y tu progreso de configuración.</Punto>
          <Punto><strong>Vistas</strong> -- Tablero (panel financiero detallado), Calendario, Informes y Voz.</Punto>
          <Punto><strong>Registros</strong> -- donde cargas información: Cuentas, Transacciones, Categorías, Deuda, Presupuestos, Recurrentes, Inversión.</Punto>
          <Punto><strong>Recursos</strong> -- esta guía y Soporte, disponibles en cualquier momento.</Punto>
        </List>
      </Section>

      <Section icon={CheckCircleIcon} title="4. Cuentas: registrar tus cuentas bancarias y efectivo">
        <Typography paragraph>
          Una "cuenta" en PCM es cualquier lugar donde tienes dinero disponible: una cuenta de
          ahorros, una cuenta corriente, o simplemente "Efectivo" para el dinero que manejas en
          billetera.
        </Typography>
        <List dense>
          <Paso n={1}>Ve a <strong>Registros → Cuentas</strong>.</Paso>
          <Paso n={2}>Haz clic en "Agregar cuenta".</Paso>
          <Paso n={3}>
            Ponle un nombre claro (ej. "BCP Ahorros", "Interbank Corriente", "Efectivo"), elige el
            tipo, y si quieres, el banco/entidad y los últimos 4 dígitos (esto último ayuda a que
            PCM reconozca la cuenta automáticamente si más adelante subes un estado de cuenta).
          </Paso>
          <Paso n={4}>Escribe el saldo actual y la moneda (soles o dólares).</Paso>
        </List>
        <Typography paragraph sx={{ mt: 1 }}>
          Cada transacción que registres después se puede vincular a una de estas cuentas, para que
          el saldo se mantenga siempre actualizado.
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip icon={<LockIcon fontSize="small" />} label="Cuenta de reserva" size="small" sx={{ bgcolor: goldColors.pale }} />
          <Typography variant="subtitle2" fontWeight={600}>Cuentas de reserva / ahorro intocable</Typography>
        </Box>
        <Typography paragraph>
          Si tienes una cuenta que en realidad es un fondo de emergencia, una provisión o una
          inversión que no quieres tocar día a día, márcala con el switch <strong>"Cuenta de
          reserva / ahorro intocable"</strong> al crearla o editarla. Por defecto, una cuenta de
          reserva queda <strong>fuera</strong> de la vista "todas las cuentas" en Calendario y en
          Transacciones -- así su saldo no se mezcla con tu plata de uso diario y no te confunde al
          revisar cuánto tienes disponible.
        </Typography>
        <Typography paragraph>
          Esto no significa que la cuenta desaparezca: si la seleccionas directo en el filtro de
          Cuenta (en Calendario o en Transacciones), siempre se muestra igual que cualquier otra.
          También puedes activar el toggle <strong>"Incluir reservas"</strong> en esas dos pantallas
          para verlas junto con el resto cuando lo necesites.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="5. Transacciones: registrar gastos e ingresos">
        <Typography paragraph sx={{ fontWeight: 600 }}>Hay tres formas de registrar un movimiento:</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 0.5, my: 2 }}>
          <FlowCard icon={EditNoteIcon} title="A mano" subtitle="Desde la app" />
          <FlowArrow />
          <FlowCard icon={MicIcon} title="Voz" subtitle="Grabas y listo" />
          <FlowArrow />
          <FlowCard icon={WhatsAppIcon} title="WhatsApp" subtitle="Texto o nota de voz" />
          <FlowArrow />
          <FlowCard icon={ReceiptLongIcon} title="Transacción registrada" color="#4caf50" />
        </Box>

        <Typography paragraph sx={{ mt: 1, fontWeight: 600 }}>A) A mano, desde la app</Typography>
        <List dense>
          <Paso n={1}>Ve a <strong>Registros → Transacciones</strong>.</Paso>
          <Paso n={2}>Haz clic en "Agregar" y elige si es un gasto o un ingreso.</Paso>
          <Paso n={3}>Completa el monto, la cuenta de la que salió (o a la que entró), la categoría, el proveedor/lugar y la fecha.</Paso>
        </List>

        <Typography paragraph sx={{ mt: 2, fontWeight: 600 }}>B) Por voz, dentro de la app</Typography>
        <List dense>
          <Paso n={1}>Ve a <strong>Vistas → Voz</strong>.</Paso>
          <Paso n={2}>Haz clic en el ícono del micrófono y di algo como "gasté 50 soles en el mercado" o "recibí 300 dólares de un cliente".</Paso>
          <Paso n={3}>Haz clic de nuevo para detener la grabación. PCM transcribe lo que dijiste, lo interpreta y crea la transacción automáticamente.</Paso>
        </List>

        <Typography paragraph sx={{ mt: 2, fontWeight: 600 }}>C) Por WhatsApp</Typography>
        <List dense>
          <Paso n={1}>
            Primero debes vincular tu número: ve a <strong>tu Perfil</strong> dentro de la app y
            escribe tu número de WhatsApp (con el código de tu país, ej. +51...).
          </Paso>
          <Paso n={2}>
            Únete al número de WhatsApp de PCM (tu asesor te da el número y el código para
            unirte, ej. "join spend-over").
          </Paso>
          <Paso n={3}>
            Envía un mensaje de texto o una nota de voz describiendo el movimiento, ej. "pagué 120
            soles de luz" o una nota de voz diciendo lo mismo.
          </Paso>
          <Paso n={4}>
            PCM te responde por el mismo chat confirmando qué registró. Si tienes más de una
            cuenta y no queda claro de cuál salió el dinero, te va a preguntar antes de guardar el
            movimiento -- solo responde con el nombre o el número de la cuenta que te muestre.
          </Paso>
        </List>
        <Typography paragraph sx={{ mt: 1 }}>
          En los tres casos, PCM reconoce automáticamente si mencionaste soles o dólares, y va
          aprendiendo qué categoría corresponde a cada proveedor (por ejemplo, si "Uber" siempre lo
          categorizas como "Transporte", la próxima vez lo hace solo).
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>B) Voz</strong> y <strong>C) WhatsApp</strong> son exclusivos del plan <strong>Pro
          o superior</strong> -- el plan Personal solo registra
          movimientos a mano, desde la app. Ver la sección 14, "Suscripción y planes".
        </Alert>
      </Section>

      <Section icon={CheckCircleIcon} title="6. Transferencias entre cuentas y tarjetas">
        <Typography paragraph>
          Cuando mueves dinero de una cuenta a otra -- o pagas una tarjeta de crédito con dinero de
          tu cuenta bancaria -- eso no es ni un gasto ni un ingreso real: es plata que sigue siendo
          tuya, solo que cambia de lugar. Para esto existe la <strong>Transferencia</strong>, un
          tercer botón (junto a Ingreso y Egreso) dentro del mismo diálogo de "Nueva Transacción" --
          no tiene una página propia en el menú.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 0.5, my: 2 }}>
          <FlowCard icon={RemoveCircleOutlineIcon} title="Cuenta origen" subtitle="Sale el dinero" color="#f44336" />
          <FlowArrow />
          <FlowCard icon={AddCircleOutlineIcon} title="Cuenta destino (o tarjeta)" subtitle="Entra el dinero" color="#4caf50" />
        </Box>
        <List dense>
          <Paso n={1}>Ve a <strong>Registros → Transacciones</strong> y haz clic en "Agregar".</Paso>
          <Paso n={2}>Elige el botón <strong>"Transferencia"</strong>.</Paso>
          <Paso n={3}>Elige la cuenta de origen y la cuenta destino (una tarjeta de crédito también es una cuenta en PCM), el monto y la fecha.</Paso>
        </List>
        <Typography paragraph sx={{ mt: 1 }}>
          PCM crea automáticamente dos movimientos vinculados con la categoría "Transferencia": un
          egreso en la cuenta de origen y un ingreso en la cuenta destino. Por eso una Transferencia{' '}
          <strong>no se cuenta</strong> como Ingreso ni Gasto real en tus Informes ni en tu
          Presupuesto -- pero sí actualiza el saldo de cada cuenta y sí aparece en el Calendario,
          porque el movimiento de caja es real. Si eliminas una Transferencia, se elimina el par
          completo (origen y destino) de una sola vez.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="7. Categorías: organizar y limpiar tus categorías">
        <Typography paragraph>
          Además de elegir la categoría al crear cada transacción, hay una página dedicada donde ver
          TODAS las categorías que usas -- las 7 base de la app (Alimentación, Transporte, etc.) y
          cualquiera que hayas creado tú, o que se haya guardado sola desde WhatsApp, voz, o al
          importar un estado de cuenta.
        </Typography>
        <List dense>
          <Paso n={1}>Ve a <strong>Registros → Categorías</strong>.</Paso>
          <Paso n={2}>
            Haz clic en el nombre de una categoría para <strong>renombrarla</strong>. Si en vez de
            eso escribes el nombre de una que ya existe, ambas se <strong>fusionan</strong> en una
            sola (útil si terminaste con dos parecidas, ej. "Alimentación" y "alimentacion").
          </Paso>
          <Paso n={3}>Haz clic en el emoji de una categoría para cambiarlo por otro de la grilla, o pega cualquier emoji que prefieras.</Paso>
          <Paso n={4}>Las categorías personalizadas que ya no uses se pueden eliminar -- las 7 base no, pero tampoco lo necesitan.</Paso>
        </List>
        <Typography paragraph sx={{ mt: 1 }}>
          Renombrar o fusionar una categoría actualiza automáticamente todas tus transacciones, tus
          reglas de proveedor y tus presupuestos con ese nombre -- no queda nada "huérfano" apuntando
          al nombre viejo.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="8. Deudas: tarjetas de crédito y préstamos">
        <List dense>
          <Paso n={1}>Ve a <strong>Registros → Deuda</strong>.</Paso>
          <Paso n={2}>Agrega cada tarjeta de crédito o préstamo: entidad, tipo, saldo actual, moneda y día de vencimiento.</Paso>
          <Paso n={3}>
            Si subes un estado de cuenta de tarjeta de crédito (PDF), PCM puede leerlo
            automáticamente con inteligencia artificial y cargar los movimientos por ti -- esta
            función es exclusiva del plan Pro o superior (ver sección 14).
          </Paso>
        </List>
        <Typography paragraph sx={{ mt: 1 }}>
          Registrar tus deudas te permite ver, en el Tablero, no solo cuánto tienes sino cuánto
          debes -- tu situación financiera real, no solo el saldo disponible.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="9. Presupuesto: cuánto quieres gastar por categoría">
        <Typography paragraph>
          En <strong>Registros → Presupuestos</strong> defines, para cada categoría de gasto, cuánto
          quieres gastar como máximo al mes -- así puedes ver de un vistazo si te estás pasando antes
          de que termine el mes, no recién cuando revisas tu cuenta bancaria.
        </Typography>
        <List dense>
          <Paso n={1}>Elige una categoría y escribe el monto mensual (siempre en soles).</Paso>
          <Paso n={2}>
            Si tienes gastos fijos recurrentes en esa categoría (ver sección 10, "Movimientos
            recurrentes"), vas a ver un <strong>"piso fijo"</strong> de referencia junto al monto --
            es lo que ya está comprometido cada mes por esos gastos fijos, para que no armes un
            presupuesto por debajo de lo que de todas formas vas a gastar.
          </Paso>
        </List>
        <BudgetBar />
        <List dense>
          <Paso n={3}>
            Cuando registras un gasto (a mano, por voz o por WhatsApp) que hace que te acerques o
            superes tu presupuesto mensual de esa categoría, PCM te avisa en el momento -- por
            WhatsApp, en el mismo mensaje de confirmación, o como aviso en pantalla si lo
            registraste desde la app o por voz.
          </Paso>
        </List>
        <Alert severity="info" sx={{ mt: 2 }}>
          Las alertas de presupuesto y el <strong>constructor de presupuesto con IA</strong> (te
          sugiere cuánto gastar por categoría según tu historial, y qué recortar) son exclusivos del
          plan <strong>Pro o superior</strong>. Con el plan Personal puedes crear y ver tus
          presupuestos igual, pero sin las alertas automáticas ni la sugerencia de IA.
        </Alert>
      </Section>

      <Section icon={CheckCircleIcon} title="10. Movimientos recurrentes">
        <Typography paragraph>
          Son gastos o ingresos que se repiten todos los meses (ej. alquiler, sueldo, un servicio de
          streaming). Ve a <strong>Registros → Recurrentes</strong> y regístralos una sola vez --
          PCM los va a considerar automáticamente en tus proyecciones y en el Calendario, sin que
          tengas que volver a cargarlos cada mes.
        </Typography>
        <Typography paragraph>
          Cada recurrente se guarda en la moneda en la que ocurre (soles o dólares) -- la lista los
          muestra en dos columnas separadas, Soles y Dólares, sin convertir uno al otro (igual que en
          Transacciones): así nunca se mezclan dos monedas distintas en un mismo total.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="11. Calendario">
        <Typography paragraph>
          En <strong>Vistas → Calendario</strong> ves, día por día, tus movimientos ya registrados y
          los que vienen (vencimientos de deudas, recurrentes programados). Es la forma más rápida
          de anticipar qué días vas a necesitar más dinero disponible.
        </Typography>
        <List dense>
          <Punto>
            Un <strong>toggle Soles/Dólares</strong> arriba del calendario convierte todo a una sola
            moneda usando el tipo de cambio del día -- el calendario es la única vista de la app que
            SÍ convierte entre monedas (el resto, como Transacciones, siempre las muestra por
            separado). El tipo de cambio usado y su fuente se muestran siempre, justo debajo del
            toggle.
          </Punto>
          <Punto>
            Haz clic en cualquier día para ver el detalle de sus movimientos -- desde ahí puedes
            <strong> editar o eliminar</strong> una transacción directamente, sin tener que ir a
            Transacciones a buscarla.
          </Punto>
          <Punto>
            Dentro del detalle de cada día ves el <strong>balance total</strong> de inicio y de fin,
            y debajo el <strong>saldo de cada cuenta por separado</strong> a esa fecha -- así sabes
            no solo cuánto tienes en total, sino en cuál cuenta específica.
          </Punto>
        </List>
        <DiaMockup />
        <Typography paragraph sx={{ mt: 1 }}>
          Si marcaste alguna cuenta como <strong>"de reserva"</strong> (ver sección 4), esa cuenta
          queda fuera de este cálculo agregado por defecto -- actívala con el toggle
          <strong> "Incluir reservas"</strong>, junto al selector de Cuenta, o selecciónala
          directamente en ese mismo selector para verla igual que cualquier otra.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="12. Informes">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ textAlign: 'center' }}>
            <MiniBarChart />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Ejemplo: barras</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <MiniPieChart />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Ejemplo: circular</Typography>
          </Box>
        </Box>
        <Typography paragraph>
          En <strong>Vistas → Informes</strong> encuentras, disponibles para todos los planes:
        </Typography>
        <List dense>
          <Punto><strong>Resumen mensual, anual, por categoría y de tendencia</strong> -- con sus números y un gráfico de barras, como el de arriba.</Punto>
          <Punto><strong>Gasto por categoría</strong> -- un gráfico circular que muestra en qué se te va la plata, de un vistazo.</Punto>
          <Punto><strong>Presupuesto vs. gastado</strong> -- compara, categoría por categoría, cuánto presupuestaste contra cuánto llevas gastado en el mes.</Punto>
          <Punto><strong>Deuda</strong> -- el total que debes y el detalle de cada tarjeta o préstamo.</Punto>
        </List>
        <Typography paragraph>
          También puedes generar un <strong>resumen narrativo con inteligencia artificial</strong>
          (un texto que te explica en pocas líneas cómo te fue en el periodo elegido) y usar
          <strong> "Pregúntale a la IA"</strong>, donde escribes cualquier pregunta libre sobre tus
          finanzas (ej. "¿cuánto gasto en promedio en comida?") y recibes una respuesta con gráficos
          cuando corresponde.
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          Los resúmenes con IA y "Pregúntale a la IA" son exclusivos del plan <strong>Pro o
          superior</strong> -- consumen además tu cuota mensual de páginas (ver sección 14). El
          resto de los informes de esta sección (barras, circular, presupuesto vs. gastado, deuda)
          están disponibles en todos los planes.
        </Alert>
      </Section>

      <Section icon={CheckCircleIcon} title="13. Perfil y configuración de cuenta">
        <Typography paragraph>
          Desde tu <strong>Perfil</strong> (haz clic en tu nombre arriba del menú) puedes actualizar
          tus datos personales, tu número de WhatsApp, y tu contraseña.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="14. Suscripción y planes">
        <Typography paragraph>
          Toda cuenta nueva empieza con una <strong>prueba gratuita de 14 días</strong>, sin
          necesidad de tarjeta, con acceso a prácticamente toda la app. Cuando vence, puedes activar
          tu plan desde <strong>Suscripción</strong> (dentro de Configuración) para seguir usando PCM
          sin interrupciones.
        </Typography>
        <Typography paragraph sx={{ fontWeight: 600, mb: 0.5 }}>Hay dos planes:</Typography>
        <List dense>
          <Punto><strong>Personal</strong> -- lo esencial: cuentas, transacciones a mano, deudas, presupuestos, informes básicos, e importador de CSV/Excel.</Punto>
          <Punto><strong>Pro</strong> -- todo lo de Personal, más registro por voz y WhatsApp, importador de estados de cuenta con IA, alertas y auto-categorización con IA, resúmenes e informes con IA, y el constructor de presupuesto con IA.</Punto>
        </List>
        <Typography paragraph sx={{ mt: 1 }}>
          Los precios y el detalle completo de cada plan están siempre actualizados en la página de
          <strong> Suscripción</strong>. Ambos planes tienen una cuota de páginas de estados de
          cuenta que se puede procesar con IA al mes (se puede comprar más si hace falta). PCM es
          para finanzas personales; si tienes un negocio, sus finanzas se manejan en CapitalOptima.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="15. Seguridad y privacidad de tus datos">
        <Typography paragraph>
          Tu información financiera se guarda cifrada, tanto mientras viaja por internet como
          guardada en el servidor, en infraestructura auditada bajo los estándares SOC 2 Tipo 2 e
          ISO 27001. Cada cuenta está aislada a nivel de base de datos -- tus datos solo son
          visibles para ti, ni siquiera otros usuarios de la plataforma tienen acceso.
        </Typography>
      </Section>

      <Section icon={CheckCircleIcon} title="16. Preguntas frecuentes">
        <Typography paragraph sx={{ fontWeight: 600, mb: 0.5 }}>
          Registré un movimiento por WhatsApp pero no me llegó ninguna respuesta.
        </Typography>
        <Typography paragraph>
          Espera unos segundos -- el servidor a veces "despierta" y tarda un momento. Si después de
          un par de minutos sigue sin responder, contacta a Soporte.
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography paragraph sx={{ fontWeight: 600, mb: 0.5 }}>
          ¿Puedo usar PCM sin cargar mis cuentas bancarias?
        </Typography>
        <Typography paragraph>
          Sí, pero vas a ver un cuadro incompleto -- las transacciones sin cuenta vinculada igual se
          registran, pero PCM no puede mostrarte saldos reales por cuenta.
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography paragraph sx={{ fontWeight: 600, mb: 0.5 }}>
          ¿Qué pasa si me equivoco al registrar un movimiento?
        </Typography>
        <Typography paragraph>
          Entra a Registros → Transacciones, búscalo y edítalo o elimínalo directamente. También
          puedes editarlo o eliminarlo desde el Calendario, haciendo clic en el día correspondiente.
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography paragraph sx={{ fontWeight: 600, mb: 0.5 }}>
          ¿Cómo registro el pago de mi tarjeta de crédito?
        </Typography>
        <Typography paragraph>
          Con una Transferencia (ver sección 6): la cuenta de origen es de donde sale la plata (tu
          cuenta bancaria) y la cuenta destino es tu tarjeta de crédito -- las tarjetas también son
          una "cuenta" dentro de PCM. Así el pago no se cuenta como un gasto nuevo, porque ya lo era
          cuando hiciste el consumo original.
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography paragraph sx={{ fontWeight: 600, mb: 0.5 }}>
          ¿Por qué no veo el saldo de mi cuenta de ahorros en Calendario o Transacciones?
        </Typography>
        <Typography paragraph>
          Probablemente la marcaste como "cuenta de reserva" (ver sección 4) -- por diseño, esas
          cuentas quedan fuera de la vista general para no mezclarse con tu plata de uso diario.
          Actívala con el toggle "Incluir reservas" o selecciónala directo en el filtro de Cuenta
          para verla igual que las demás.
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography paragraph sx={{ fontWeight: 600, mb: 0.5 }}>
          Intenté usar WhatsApp/voz/el importador con IA y me salió un mensaje de que es exclusivo de otro plan.
        </Typography>
        <Typography paragraph>
          Esas funciones (junto con las alertas de presupuesto y los informes con IA) son exclusivas
          del plan Pro o superior -- ver la sección 14. Puedes actualizar tu plan en cualquier
          momento desde Suscripción.
        </Typography>
      </Section>

      <Paper elevation={0} sx={{ mt: 3, p: 2.5, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          ¿No encontraste lo que buscabas? Escríbenos desde <strong>Recursos → Soporte</strong> en
          el menú.
        </Typography>
      </Paper>
    </Container>
  );
};

export default GuidesPage;
